import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { TableSettingColumn } from 'types/tableSettingsTypes';

interface AdminProductState {
  excludedFilter: string[];
  filterData: Record<string, any>;
  reloadTableData: boolean;
  // ticked rows in the product list (bulk status change)
  selectedProductIds: string[];
  // which Products page tab the filters above belong to
  listScope: string | null;
  // the user's saved table settings per Products page tab (by table key); a tab without any
  // uses its default columns from AdminProductList/tabTables
  tableColumnLists: Record<string, TableSettingColumn[]>;
}

const initialState: AdminProductState = {
  excludedFilter: ['search'],
  filterData: {},
  reloadTableData: false,
  selectedProductIds: [],
  listScope: null,
  tableColumnLists: {},
};

const adminProductSlice = createSlice({
  name: 'adminProduct',
  initialState,
  reducers: {
    setFilterData(state, action: PayloadAction<Record<string, any>>) {
      state.filterData = { ...state.filterData, ...action.payload };
    },
    resetFilterData(state) {
      state.filterData = {};
    },
    removeFilterKey(state, action: PayloadAction<string>) {
      delete state.filterData[action.payload];
    },
    setReloadTableData(state, action: PayloadAction<boolean>) {
      state.reloadTableData = action.payload;
    },
    setSelectedProductIds(state, action: PayloadAction<string[]>) {
      state.selectedProductIds = action.payload;
    },
    // The Products page tabs share this slice - moving to another tab starts with no
    // filters / ticked rows instead of carrying the last tab's over.
    setProductListScope(state, action: PayloadAction<string>) {
      if (state.listScope !== action.payload) {
        state.listScope = action.payload;
        state.filterData = {};
        state.selectedProductIds = [];
      }
    },
    setProductTableColumnList(
      state,
      action: PayloadAction<{ tableKey: string; columns: TableSettingColumn[] }>
    ) {
      state.tableColumnLists[action.payload.tableKey] = action.payload.columns;
    },
  },
});

export const {
  setFilterData,
  resetFilterData,
  setReloadTableData,
  setSelectedProductIds,
  setProductListScope,
  setProductTableColumnList,
  removeFilterKey,
} = adminProductSlice.actions;

export default adminProductSlice.reducer;
